import { Box, Column, Txt } from "@stenajs-webui/core";
import {
  Banner,
  Link,
  PrimaryButton,
  stenaCheck,
} from "@stenajs-webui/elements";
import {
  ModalActionButtons,
  ModalBody,
  ModalHeader,
} from "@stenajs-webui/modal";
import type { FC } from "react";

import { Information } from "../../../../common/texts";
import {
  type TrackerCategory,
  trackEvent,
} from "../../../../common/tracking/trackerService";
import { useCustomsLinkTokenLoader } from "../../hooks/useCustomsLinkTokenLoader";

interface Props {
  bookingNo: number;
  onClose: () => void;
  trackerCategory: TrackerCategory;
}

export const CustomsLinkGdprModal: FC<Props> = ({
  onClose,
  bookingNo,
  trackerCategory,
}) => {
  const { fetchTokenAndRedirect, acceptGdpr, loaderState } =
    useCustomsLinkTokenLoader(bookingNo);

  const onClickCustomsLink = async () => {
    if (await acceptGdpr()) {
      trackEvent({
        category: trackerCategory,
        action: "Accepted CustomsLink GDPR",
      });

      if (await fetchTokenAndRedirect(bookingNo)) {
        onClose();
      }
    }
  };

  return (
    <>
      <ModalBody width={[null, null, 450]} gap={0}>
        <Box gap={3}>
          <ModalHeader
            heading={Information.CustomsLinkSignupHeading}
            onClickClose={onClose}
            closeButtonAriaLabel={"Close"}
          />
          <Column gap={2}>
            <Txt>{Information.CustomsLinkSignupIntro}</Txt>
            <Txt>{Information.CustomsLinkSignupBenefits}</Txt>
            <Txt size={"small"}>
              When you sign up, Stena Line will send your email and account name
              to CustomsLink. For more information, see the{" "}
              <Link
                href="https://www.customs-link.com/sites/default/files/2022-06/CustomsLink_Terms_and_Conditions.pdf"
                target={"_blank"}
                size={"small"}
              >
                Terms and conditions
              </Link>{" "}
              and{" "}
              <Link
                href="https://www.customs-link.com/privacy-policy"
                target={"_blank"}
                size={"small"}
              >
                Privacy Policy
              </Link>
              .
            </Txt>
          </Column>
          {loaderState?.name === "error" && (
            <Banner variant={"error"} text={loaderState.error} />
          )}
        </Box>
        <ModalActionButtons>
          <PrimaryButton
            label={Information.CustomsLinkSignupCta}
            leftIcon={stenaCheck}
            onClick={onClickCustomsLink}
            loading={loaderState?.name === "loading"}
            loadingLabel={Information.ContactingCustomsLink}
          />
        </ModalActionButtons>
      </ModalBody>
    </>
  );
};
